import { useCallback, useEffect, useMemo, useState } from 'react';
import { ComponentDimensions } from '../../../../types/Layout';
import CandleSticksChart from '../../../../components/Charts/CandleSticks';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import useRequest from '../../../../hooks/useRequest';
import { Scale } from '../../../../types/Chart';
import { PriceChartApiResponse } from '../../../Detail/Charts';
import { Bot, CandleType, TimeFrame } from '../../../Detail/types';

import './index.css';
import CandlesChartOptions from './ChartOptions';
import CandlesForm from '../Form/Basic';
import { useLocation } from 'react-router-dom';
import { calculateStartTimestampByTimeFrame } from '../../../Detail/Charts/PriceChart/helpers';
import { formatPrice } from '../../../../helpers/NumberFormatter';
import axios, { Method } from 'axios';
import { formatParam } from '../../../Control/Content/helpers';
import { CandleSticksLatestOrder } from '../../../Detail/Charts/types';
import { Exchange } from '../../../../types/exchange';

import ServicePaths from '../../../../bridge/utils/ServicePaths';
// import { Indicators } from '../../types';
// import MultiChart from '../../../../components/MultiChart';
// import RSIChart from '../../../../components/Charts/Rsi';
// import MACDChart from '../../../../components/Charts/MACD';
// import BandCDChart from '../../../../components/Charts/BandCD';
// import MarketCipherWaves from '../../../../components/Charts/MarketCipherWaves';
import LoadingIcons from 'react-loading-icons';
import { colors } from '../../../../colors';

const formatDateToUsableInputValue = (date: Date) => date.toJSON().slice(0, 10);

interface Props {
  rootDimensions: ComponentDimensions;
}

interface PassedProps {
  baseAsset: string;
  candleType: CandleType;
  quoteAsset: string;
  scale: Scale;
  timeFrame: TimeFrame;
  bot: Bot;
  candleSticksLatestOrder?: CandleSticksLatestOrder;
  exchange: Exchange;
}

const Candles = (props: Props) => {
  const { rootDimensions } = props;

  const { state: passedProps }: { state: PassedProps | undefined } =
    useLocation();

  const [isCollapsable, setIsCollapsable] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [baseAsset, setBaseAsset] = useState(passedProps?.baseAsset || '');
  const [quoteAsset, setQuoteAsset] = useState(
    passedProps?.quoteAsset || 'USDT',
  );
  const [exchange, setExchange] = useState(
    passedProps?.exchange || Object.values(Exchange)[0],
  );

  const [timeFrame, setTimeFrame] = useState(
    passedProps?.timeFrame || TimeFrame.OneWeek,
  );
  const [startDate, setStartDate] = useState(
    !!passedProps
      ? formatDateToUsableInputValue(
          new Date(calculateStartTimestampByTimeFrame(passedProps?.timeFrame)),
        )
      : '2022-01-01',
  );
  const [endDate, setEndDate] = useState(
    formatDateToUsableInputValue(
      new Date(new Date().valueOf() + 1000 * 60 * 60 * 24),
    ),
  );
  const [candleSticksLatestOrder, setCandleSticksLatestOrder] = useState(
    passedProps?.candleSticksLatestOrder || undefined,
  );

  const [scale, setScale] = useState(passedProps?.scale || Scale.Log);
  const [candleType, setCandleType] = useState(
    passedProps?.candleType || CandleType.Heikin,
  );
  const [bot, setBot] = useState<Bot | undefined>(passedProps?.bot);

  const [shouldFetch, setShouldFetch] = useState(!!passedProps);

  const params = useMemo(
    () => ({
      baseAsset,
      quoteAsset,
      timeFrame,
      startTimestamp: new Date(startDate).valueOf(),
      endTimestamp: new Date(endDate).valueOf(),
      includeLatestCandle: true,
      exchange,
    }),
    [baseAsset, endDate, quoteAsset, startDate, timeFrame, exchange],
  );
  const options = useMemo(
    () => ({ method: 'GET' as Method, shouldFetch }),
    [shouldFetch],
  );

  const {
    data,
    isValidating,
  }: { data: PriceChartApiResponse | undefined; isValidating: boolean } =
    useRequest(ServicePaths.CANDLE_FETCHER.FetchHeikinCandles, params, options);

  useEffect(() => {
    if (data) {
      setShouldFetch(false);
    }
  }, [data, shouldFetch]);

  const currentPrice = useMemo(() => {
    const currentPriceValue = data?.normal?.[data?.normal?.length - 1]?.close;

    if (!currentPriceValue) {
      return undefined;
    }

    return formatPrice(currentPriceValue, true, 4);
  }, [data?.normal]);

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    const fetch = async () => {
      try {
        const result = await axios({
          method: 'GET',
          url: ServicePaths.CONTROL.GetRawBot,
          params: {
            baseAsset: formatParam(baseAsset),
            quoteAsset: formatParam(quoteAsset),
            higherTimeFrame: formatParam(timeFrame),
          },
        });

        if (result?.data) {
          setBot(result.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    /** Fetch the bot
     * Take the cbrt
     */
    fetch();
  }, [baseAsset, quoteAsset, shouldFetch, timeFrame]);

  useEffect(() => {
    if (isCollapsable || !data) {
      return;
    }

    setIsCollapsable(true);
    setIsCollapsed(true);
  }, [data, isCollapsable]);

  const toggleCollapse = useCallback(() => {
    if (!isCollapsable) {
      return;
    }

    setIsCollapsed(!isCollapsed);
  }, [isCollapsable, isCollapsed]);

  return (
    <>
      <div
        className="single-profit-calculation-header"
        onClick={toggleCollapse}
      >
        <h3 className="single-profit-calculation-label">Form:</h3>
        {isValidating ? (
          <div className="single-profit-calculation-loader">
            <LoadingIcons.Grid width={20} height={20} />
          </div>
        ) : (
          isCollapsable &&
          (isCollapsed ? (
            <FaChevronDown
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ) : (
            <FaChevronUp
              color={colors.white}
              size={24}
              className={'chevron chevron-down'}
            />
          ))
        )}
      </div>

      {!isCollapsed && (
        <>
          <CandlesForm
            baseAsset={baseAsset}
            setBaseAsset={setBaseAsset}
            quoteAsset={quoteAsset}
            setQuoteAsset={setQuoteAsset}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setCandleSticksLatestOrder={setCandleSticksLatestOrder}
            setShouldFetch={setShouldFetch}
            exchange={exchange}
            setExchange={setExchange}
          />
          <CandlesChartOptions
            scale={scale}
            setScale={setScale}
            candleType={candleType}
            setCandleType={setCandleType}
            timeFrame={timeFrame}
            setTimeFrame={setTimeFrame}
            setShouldFetch={setShouldFetch}
          />
        </>
      )}

      <div
        className="profit-calcs-candles-divider"
        style={isCollapsed ? { marginTop: 0 } : {}}
      />

      {data && (
        <div className="profit-calcs-candles-container">
          <CandleSticksChart
            baseAsset={baseAsset}
            quoteAsset={quoteAsset}
            timeFrame={timeFrame}
            exchange={exchange}
            candleType={candleType}
            candleResponse={data}
            shouldFetch={shouldFetch}
            isLoading={isValidating && shouldFetch}
            dimensions={rootDimensions}
            scale={scale}
            currentPrice={currentPrice}
            // bot={bot}
            candleSticksLatestOrder={candleSticksLatestOrder}
          />
        </div>
      )}
    </>
  );
};

export default Candles;
